import { Box, Button, Flex, Text, TextArea, TextField } from "@radix-ui/themes";
import { type FormEvent, useContext, useEffect, useId, useState } from "react";
import {
	Locale,
	LocalizationContext,
	type LocalizedStrings,
	useLocalization,
} from "~/components/localization/LocalizationProvider";
import { submitConsultLead } from "~/lib/consult.functions";
import type { ConsultFormFieldErrors, FormSource } from "~/lib/leadsigma";
import styles from "./base-consult-form.module.css";

type ConsultFormLocalization = {
	fullNamePlaceholder: string;
	emailPlaceholder: string;
	phonePlaceholder: string;
	messagePlaceholder: string;
	transformMySmile: string;
	sending: string;
	successTitle: string;
	successBody: string;
	errorBody: string;
	required: string;
	invalidEmail: string;
	invalidPhone: string;
	tooLong: string;
	partialBody: string;
	guardian: string;
	patient: string;
	referrer: string;
	referredPatient: string;
	comments: string;
	submit: string;
	requestReceived: string;
};

const englishConsultFormLocalization: ConsultFormLocalization = {
	fullNamePlaceholder: "Full Name",
	emailPlaceholder: "Email",
	phonePlaceholder: "Phone",
	messagePlaceholder: "Comments (optional)",
	transformMySmile: "Transform My Smile",
	sending: "Sending…",
	successTitle: "Thank you!",
	successBody:
		"We received your request and will reach out shortly to schedule your consultation.",
	errorBody:
		"We could not confirm your request. Please call 305-407-9000 before submitting again.",
	required: "This field is required.",
	invalidEmail: "Please enter a valid email address.",
	invalidPhone: "Please enter a valid phone number.",
	tooLong: "This entry is too long. Please shorten it.",
	partialBody:
		"Your request reached one of our systems, but delivery to the other could not be confirmed. Please do not resubmit; call 305-407-9000 to confirm receipt.",
	guardian: "Parent/Legal Guardian",
	patient: "Patient's Full Name",
	referrer: "Your Name",
	referredPatient: "Full name of the patient you are referring",
	comments: "Comments",
	submit: "Submit",
	requestReceived:
		"We received your request. Our team will follow up with you.",
};

const spanishConsultFormLocalization: ConsultFormLocalization = {
	fullNamePlaceholder: "Nombre Completo",
	emailPlaceholder: "Correo Electrónico",
	phonePlaceholder: "Teléfono",
	messagePlaceholder: "Comentarios (opcional)",
	transformMySmile: "Transforma Mi Sonrisa",
	sending: "Enviando…",
	successTitle: "¡Gracias!",
	successBody:
		"Recibimos tu solicitud y nos comunicaremos contigo pronto para programar tu consulta.",
	errorBody:
		"No pudimos confirmar tu solicitud. Llama al 305-407-9000 antes de volver a enviarla.",
	required: "Este campo es obligatorio.",
	invalidEmail: "Ingresa un correo electrónico válido.",
	invalidPhone: "Ingresa un número de teléfono válido.",
	tooLong: "Este texto es demasiado largo. Por favor, acórtalo.",
	partialBody:
		"Tu solicitud llegó a uno de nuestros sistemas, pero no pudimos confirmar la entrega al otro. No la envíes de nuevo; llama al 305-407-9000 para confirmar la recepción.",
	guardian: "Nombre completo del padre o tutor legal",
	patient: "Nombre completo del paciente",
	referrer: "Tu nombre",
	referredPatient: "Nombre completo del paciente que recomiendas",
	comments: "Comentarios",
	submit: "Enviar",
	requestReceived:
		"Recibimos tu solicitud. Nuestro equipo se pondrá en contacto contigo.",
};

const localized: LocalizedStrings<ConsultFormLocalization> = {
	[Locale.EN]: englishConsultFormLocalization,
	[Locale.ES]: spanishConsultFormLocalization,
};

type FormStatus = "idle" | "submitting" | "success" | "partial" | "error";

type BaseConsultFormProps = {
	/** Identifies which form on the site the lead came from. */
	source: FormSource;
	showMessage?: boolean;
};

export function BaseConsultForm({
	source,
	showMessage = false,
}: BaseConsultFormProps) {
	const getLocalization = useLocalization(localized);
	const { locale } = useContext(LocalizationContext);
	const isKids = source === "kids-club";
	const isReferral = source === "patients-refer-a-friend";
	const nameLabel = getLocalization(
		isKids ? "guardian" : isReferral ? "referrer" : "fullNamePlaceholder",
	);
	const patientLabel = getLocalization(isKids ? "patient" : "referredPatient");
	const id = useId();
	const [status, setStatus] = useState<FormStatus>("idle");
	const [errors, setErrors] = useState<ConsultFormFieldErrors>({});
	// Flipped once React has attached the submit handler, so a click before
	// hydration can't fall through to a native GET submit. Tests wait on it.
	const [hydrated, setHydrated] = useState(false);
	useEffect(() => setHydrated(true), []);

	const errorMessage = (field: keyof ConsultFormFieldErrors) => {
		const code = errors[field];
		if (!code) return null;
		if (code === "required") return getLocalization("required");
		if (code === "tooLong") return getLocalization("tooLong");
		if (field === "email") return getLocalization("invalidEmail");
		return getLocalization("invalidPhone");
	};

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (status === "submitting") return;
		const form = event.currentTarget;
		const formData = new FormData(form);
		const read = (key: string) => String(formData.get(key) ?? "");

		setStatus("submitting");
		setErrors({});
		try {
			const result = await submitConsultLead({
				data: {
					fullName: read("fullName"),
					email: read("email"),
					phone: read("phone"),
					message: read("message"),
					patientName: read("patientName"),
					website: read("website"),
					source,
					locale,
				},
			});
			if (result.status === "ok") {
				setStatus("success");
				form.reset();
			} else if (result.status === "partial") {
				setStatus("partial");
			} else if (result.status === "invalid") {
				setErrors(result.errors);
				setStatus("idle");
			} else {
				setStatus("error");
			}
		} catch {
			setStatus("error");
		}
	}

	if (status === "success") {
		return (
			<Flex
				direction="column"
				gapY="2"
				role="status"
				data-testid="consult-form-success"
			>
				<Text size="5" weight="bold">
					{getLocalization("successTitle")}
				</Text>
				<Text>
					{getLocalization(
						isKids || isReferral ? "requestReceived" : "successBody",
					)}
				</Text>
			</Flex>
		);
	}

	if (status === "partial") {
		return (
			<Text as="p" role="alert" data-testid="consult-form-partial">
				{getLocalization("partialBody")}
			</Text>
		);
	}

	return (
		<form
			method="post"
			onSubmit={handleSubmit}
			noValidate
			data-testid="consult-form"
			data-hydrated={hydrated ? "true" : "false"}
		>
			<Flex direction="column" gapY="4">
				<Flex direction="column" gapY="4">
					<Field error={errorMessage("fullName")} id={`${id}-fullName`}>
						<TextField.Root
							id={`${id}-fullName`}
							name="fullName"
							size="3"
							autoComplete="name"
							placeholder={nameLabel}
							aria-label={nameLabel}
							maxLength={200}
							aria-describedby={
								errors.fullName ? `${id}-fullName-error` : undefined
							}
							aria-invalid={Boolean(errors.fullName)}
							required
						/>
					</Field>
					{isKids || isReferral ? (
						<Field error={errorMessage("patientName")} id={`${id}-patientName`}>
							<TextField.Root
								id={`${id}-patientName`}
								name="patientName"
								size="3"
								placeholder={patientLabel}
								aria-label={patientLabel}
								maxLength={200}
								required
								aria-invalid={Boolean(errors.patientName)}
								aria-describedby={
									errors.patientName ? `${id}-patientName-error` : undefined
								}
							/>
						</Field>
					) : null}
					<Field error={errorMessage("email")} id={`${id}-email`}>
						<TextField.Root
							id={`${id}-email`}
							name="email"
							maxLength={200}
							aria-describedby={errors.email ? `${id}-email-error` : undefined}
							type="email"
							size="3"
							autoComplete="email"
							placeholder={getLocalization("emailPlaceholder")}
							aria-label={getLocalization("emailPlaceholder")}
							aria-invalid={Boolean(errors.email)}
							required
						/>
					</Field>
					<Field error={errorMessage("phone")} id={`${id}-phone`}>
						<TextField.Root
							id={`${id}-phone`}
							name="phone"
							maxLength={40}
							aria-describedby={errors.phone ? `${id}-phone-error` : undefined}
							type="tel"
							size="3"
							autoComplete="tel"
							placeholder={getLocalization("phonePlaceholder")}
							aria-label={getLocalization("phonePlaceholder")}
							aria-invalid={Boolean(errors.phone)}
							required
						/>
					</Field>
					{showMessage || isReferral ? (
						<Field error={errorMessage("message")} id={`${id}-message`}>
							<TextArea
								id={`${id}-message`}
								name="message"
								size="3"
								rows={3}
								placeholder={getLocalization(
									isReferral ? "comments" : "messagePlaceholder",
								)}
								aria-label={getLocalization(
									isReferral ? "comments" : "messagePlaceholder",
								)}
								required={isReferral}
								maxLength={2000}
								aria-invalid={Boolean(errors.message)}
								aria-describedby={
									errors.message ? `${id}-message-error` : undefined
								}
							/>
						</Field>
					) : null}
					{/* Honeypot: hidden from people, filled by bots. */}
					<div className={styles.honeypot} aria-hidden="true">
						<label htmlFor={`${id}-website`}>Website</label>
						<input
							id={`${id}-website`}
							name="website"
							type="text"
							tabIndex={-1}
							autoComplete="off"
						/>
					</div>
				</Flex>
				{status === "error" ? (
					<Text color="red" role="alert" data-testid="consult-form-error">
						{getLocalization("errorBody")}
					</Text>
				) : null}
				<Box width="fit-content">
					<Button
						type="submit"
						size="3"
						className={styles.submit}
						disabled={!hydrated || status === "submitting"}
					>
						{status === "submitting"
							? getLocalization("sending")
							: getLocalization(isReferral ? "submit" : "transformMySmile")}
					</Button>
				</Box>
			</Flex>
		</form>
	);
}

function Field({
	children,
	error,
	id,
}: {
	children: React.ReactNode;
	error: string | null;
	id: string;
}) {
	return (
		<Flex direction="column" gapY="1">
			{children}
			{error ? (
				<Text size="2" color="red" id={`${id}-error`}>
					{error}
				</Text>
			) : null}
		</Flex>
	);
}
